export const LOCXX_TASK_TEMPLATE_CODE = 'locxxA';

export const LOCXX_EXPANSION_MESSAGE_SCENE = 'locxxExpansionTaskDetail';

export const LOCXX_EXPANSION_ROUTE_MAP = {
  pc: '/expandstore/expansiontask',
  h5: '/standardstore/taskdetail',
} as const;

export const LOCXX_EXPANSION_PAGE_SCENES = {
  taskDetail: {
    scene: LOCXX_EXPANSION_MESSAGE_SCENE,
    pc: '/expandstore/expansiontask',
    h5: '/standardstore/taskdetail',
  },
  taskList: {
    scene: 'locxxExpansionTaskList',
    pc: '/expandstore/expansiontask',
    h5: '/standardstore/tasklist',
  },
  approvalWorkbench: {
    scene: 'locxxApprovalWorkbench',
    pc: '/expandstore/approver',
    h5: '/approval',
  },
} as const;

export const LOCXX_EXPANSION_INSIGHT_REDIRECT_PATH = '/login/locxxredirect';

export interface LocxxExpansionMessageParams {
  scene?: string;
  taskId?: string | number;
  tenantId?: string | number;
  template?: string;
  mobileRouter?: string;
  appId?: string;
  appID?: string;
  corpId?: string;
  clientId?: string;
}

type LocxxExpansionPageScene = typeof LOCXX_EXPANSION_PAGE_SCENES[keyof typeof LOCXX_EXPANSION_PAGE_SCENES];

function isPositiveInteger(value?: string | number) {
  return /^[1-9]\d*$/.test(`${value || ''}`);
}

export function isLocxxExpansionTaskMessage(pathname: string, params: LocxxExpansionMessageParams) {
  return pathname === LOCXX_EXPANSION_ROUTE_MAP.pc &&
    params.scene === LOCXX_EXPANSION_MESSAGE_SCENE &&
    params.template === LOCXX_TASK_TEMPLATE_CODE &&
    isPositiveInteger(params.taskId) &&
    isPositiveInteger(params.tenantId);
}

export function buildLocxxExpansionH5TaskUrl(baseUrl: string, params: LocxxExpansionMessageParams) {
  if (!isPositiveInteger(params.taskId) || params.template !== LOCXX_TASK_TEMPLATE_CODE) return null;
  const searchParams = new URLSearchParams({
    id: `${params.taskId}`,
    template: LOCXX_TASK_TEMPLATE_CODE,
  });
  return `${baseUrl}${LOCXX_EXPANSION_ROUTE_MAP.h5}?${searchParams.toString()}`;
}

export function getLocxxExpansionPageScene(pathname: string, params: LocxxExpansionMessageParams): LocxxExpansionPageScene | null {
  const scene = Object.values(LOCXX_EXPANSION_PAGE_SCENES).find((item) => item.scene !== LOCXX_EXPANSION_MESSAGE_SCENE && item.pc === pathname && item.scene === params.scene);
  if (!scene || !isPositiveInteger(params.tenantId)) return null;
  return scene;
}

export function buildLocxxExpansionH5PageUrl(baseUrl: string, params: LocxxExpansionMessageParams) {
  const scene = Object.values(LOCXX_EXPANSION_PAGE_SCENES).find((item) => item.scene === params.scene);
  if (!scene || !/^[1-9]\d*$/.test(`${params.tenantId || ''}`)) return null;
  if (params.scene === LOCXX_EXPANSION_MESSAGE_SCENE) return buildLocxxExpansionH5TaskUrl(baseUrl, params);
  const searchParams = new URLSearchParams({
    scene: scene.scene,
    tenantId: `${params.tenantId}`,
  });
  return `${baseUrl}${scene.h5}?${searchParams.toString()}`;
}

export function buildLocxxExpansionInsightRedirectUrl(baseUrl: string, params: LocxxExpansionMessageParams) {
  const scene = Object.values(LOCXX_EXPANSION_PAGE_SCENES).find((item) => item.scene === params.scene);
  if (!/^[1-9]\d*$/.test(`${params.tenantId || ''}`)) return null;
  if (params.scene === LOCXX_EXPANSION_MESSAGE_SCENE) {
    if (!isPositiveInteger(params.taskId) || params.template !== LOCXX_TASK_TEMPLATE_CODE) return null;
    return `${baseUrl}${LOCXX_EXPANSION_INSIGHT_REDIRECT_PATH}?${new URLSearchParams({
      scene: LOCXX_EXPANSION_MESSAGE_SCENE,
      tenantId: `${params.tenantId}`,
      taskId: `${params.taskId}`,
      template: LOCXX_TASK_TEMPLATE_CODE,
    }).toString()}`;
  }
  if (!scene) return null;
  const searchParams = new URLSearchParams({
    scene: scene.scene,
    tenantId: `${params.tenantId}`,
  });
  return `${baseUrl}${LOCXX_EXPANSION_INSIGHT_REDIRECT_PATH}?${searchParams.toString()}`;
}

export function getLocxxExpansionPageCleanPath(pathname: string, search: string, hash = '') {
  const searchParams = new URLSearchParams(search);
  const params = Object.fromEntries(searchParams.entries());
  if (isLocxxExpansionTaskMessage(pathname, params)) {
    searchParams.delete('scene');
    searchParams.delete('tenantId');
    searchParams.delete('taskId');
    searchParams.delete('template');
    searchParams.set('id', `${params.taskId}`);
    const cleanSearch = searchParams.toString();
    return `${pathname}${cleanSearch ? `?${cleanSearch}` : ''}${hash}`;
  }
  if (!getLocxxExpansionPageScene(pathname, params)) return null;
  searchParams.delete('scene');
  searchParams.delete('tenantId');
  const cleanSearch = searchParams.toString();
  return `${pathname}${cleanSearch ? `?${cleanSearch}` : ''}${hash}`;
}

export function getLocxxExpansionReturnPath(pathname: string, search: string, hash = '') {
  const params = Object.fromEntries(new URLSearchParams(search).entries());
  if (!isLocxxExpansionTaskMessage(pathname, params) && !getLocxxExpansionPageScene(pathname, params)) return null;
  return `${pathname}${search.startsWith('?') ? search : `?${search}`}${hash}`;
}

export function getLocxxExpansionPcAction(
  pathname: string,
  search: string,
  currentTenantId?: string | number,
  token?: string,
  hash = '',
) {
  const originUrl = getLocxxExpansionReturnPath(pathname, search, hash);
  const params = Object.fromEntries(new URLSearchParams(search).entries());
  if (!originUrl) {
    if ((pathname === LOCXX_EXPANSION_ROUTE_MAP.pc && params.scene === LOCXX_EXPANSION_MESSAGE_SCENE) ||
      Object.values(LOCXX_EXPANSION_PAGE_SCENES).some((item) => item.pc === pathname && item.scene === params.scene)) {
      return { type: 'fallback' as const, tenantId: `${params.tenantId || ''}`, originUrl: '/' };
    }
    return null;
  }
  const tenantId = `${params.tenantId}`;
  if (!token) return { type: 'login' as const, tenantId, originUrl };
  if (`${currentTenantId || ''}` !== tenantId) return { type: 'switchTenant' as const, tenantId, originUrl };
  return { type: 'render' as const, tenantId, originUrl };
}
